import BigNumber from 'bignumber.js';
import {
  Chain,
  ChainSubType,
  ChainType,
  Timeframe,
} from '@ankr.com/chains-list';
import { ETelemetryTopOf } from 'multirpc-sdk';
import { useMemo } from 'react';

import { EndpointGroup } from 'modules/endpoints/types';
import { isSSG } from 'modules/common/utils/isSSG';
import {
  selectMappedPublicRequestsChartData,
  selectPublicCountriesCountsMap,
  selectPublicTotalRequests,
} from 'modules/stats/store/public/selectors';
import { useAppSelector } from 'store/useAppSelector';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { usePublicStats } from 'modules/stats/hooks/usePublicStats';
import { usePublicStatsParams } from 'modules/chains/hooks/usePublicStatsParams';

import { ChainProtocol } from '../../../../constants/ChainProtocolContext';
import { getStatsChainId } from '../../../ChainItemSections/utils/getStatsChainId';

export interface UsageDataParams {
  chain: Chain;
  chainProtocol?: ChainProtocol;
  chainSubType?: ChainSubType;
  chainType: ChainType;
  group: EndpointGroup;
  isChainProtocolSwitchEnabled?: boolean;
  timeframe: Timeframe;
}

const metrics = [ETelemetryTopOf.COUNTRY];

export const usePublicUsageData = ({
  chain,
  chainProtocol,
  chainSubType,
  chainType,
  group,
  isChainProtocolSwitchEnabled,
  timeframe,
}: UsageDataParams) => {
  const { isLoggingIn } = useAuth();

  const chainId = getStatsChainId({
    publicChain: chain,
    chainType,
    chainSubType,
    group,
    isChainProtocolSwitchEnabled,
    chainProtocol,
  });

  const { publicStatsParams } = usePublicStatsParams({
    chainId,
    metrics,
    skipFetching: isSSG(),
    timeframe,
  });

  const { loading: publicStatsLoading, params } =
    usePublicStats(publicStatsParams);

  const countries = useAppSelector(state =>
    selectPublicCountriesCountsMap(state, params),
  );
  const requestsChartData = useAppSelector(state =>
    selectMappedPublicRequestsChartData(state, params),
  );
  const totalRequests = useAppSelector(state =>
    selectPublicTotalRequests(state, params),
  );

  const totalRequestsNumber = useMemo(
    () => new BigNumber(totalRequests),
    [totalRequests],
  );

  return {
    countries,
    isLoggingIn,
    loading: publicStatsLoading || isLoggingIn || isSSG(),
    requestsChartData,
    totalRequests: totalRequestsNumber,
  };
};
